import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import type { WorkshopProjectMembers } from "@/types/workshop.type";
import {
  WORKSHOP_PROJECTS_ROOT,
  workshopMembersKey,
  workshopProjectKey,
  workshopSessionsKey,
} from "./workshopKeys";

/**
 * Collab mode: the people the project's owner added. Everyone in the project
 * can read the list; only the owner changes it (the server refuses anyone
 * else). Every change shows at once; a refused one puts back only the people
 * it touched, and the list is reconciled once the last change settles, so
 * quick clicks on several people never undo each other.
 */
export function useWorkshopMembers(projectId: string | null) {
  const qc = useQueryClient();
  const key = workshopMembersKey(projectId);
  const mutationKey = ["workshopMembersChange", projectId];
  const othersInFlight = () => qc.isMutating({ mutationKey }) > 1;

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await axiosInstance.get<WorkshopProjectMembers>(
        `/workshop/projects/${projectId}/members`,
      );
      return res.data;
    },
    enabled: !!projectId,
  });

  const settle = () => {
    if (othersInFlight()) return;
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: workshopProjectKey(projectId) });
    void qc.invalidateQueries({ queryKey: WORKSHOP_PROJECTS_ROOT });
    // Removing someone takes them out of the project's threads too.
    void qc.invalidateQueries({ queryKey: workshopSessionsKey(projectId) });
  };

  const snapshot = async () => {
    await qc.cancelQueries({ queryKey: key });
    return qc.getQueryData<WorkshopProjectMembers>(key);
  };

  const failed = (title: string) => (error: unknown) =>
    queue.add({ title, description: getAiRequestErrorMessage(error), variant: "error" });

  const addMembers = useMutation({
    mutationKey,
    mutationFn: async (people: Array<{ userId: string; name: string | null; email: string | null }>) => {
      const res = await axiosInstance.post<WorkshopProjectMembers>(
        `/workshop/projects/${projectId}/members`,
        { userIds: people.map((p) => p.userId) },
      );
      return res.data;
    },
    onMutate: async (people) => {
      const previous = await snapshot();
      const now = new Date().toISOString();
      const added = people
        .filter((p) => !previous?.members.some((m) => m.userId === p.userId))
        .map((p) => p.userId);
      qc.setQueryData<WorkshopProjectMembers>(key, (current) =>
        current
          ? {
              ...current,
              members: [
                ...current.members,
                ...people
                  .filter((p) => !current.members.some((m) => m.userId === p.userId))
                  .map((p) => ({ ...p, addedAt: now })),
              ],
            }
          : current,
      );
      return { added };
    },
    // Take out only the people this add put in.
    onError: (error, _people, context) => {
      const added = new Set(context?.added ?? []);
      qc.setQueryData<WorkshopProjectMembers>(key, (current) =>
        current
          ? { ...current, members: current.members.filter((m) => !added.has(m.userId)) }
          : current,
      );
      failed("Couldn't add them to the project")(error);
    },
    // The server's list, unless another change is still on its way to it.
    onSuccess: (data) => {
      if (!othersInFlight()) qc.setQueryData(key, data);
    },
    onSettled: settle,
  });

  const removeMember = useMutation({
    mutationKey,
    mutationFn: async (userId: string) => {
      const res = await axiosInstance.delete<WorkshopProjectMembers>(
        `/workshop/projects/${projectId}/members/${userId}`,
      );
      return res.data;
    },
    onMutate: async (userId) => {
      const previous = await snapshot();
      const removed = previous?.members.find((m) => m.userId === userId) ?? null;
      qc.setQueryData<WorkshopProjectMembers>(key, (current) =>
        current
          ? { ...current, members: current.members.filter((m) => m.userId !== userId) }
          : current,
      );
      return { removed };
    },
    // Put back only the person whose removal failed.
    onError: (error, _userId, context) => {
      const removed = context?.removed;
      if (removed) {
        qc.setQueryData<WorkshopProjectMembers>(key, (current) =>
          current && !current.members.some((m) => m.userId === removed.userId)
            ? { ...current, members: [...current.members, removed] }
            : current,
        );
      }
      failed("Couldn't remove them from the project")(error);
    },
    onSettled: settle,
  });

  /** Collab mode off: everyone out. */
  const removeAll = useMutation({
    mutationKey,
    mutationFn: async () => {
      const res = await axiosInstance.delete<WorkshopProjectMembers>(
        `/workshop/projects/${projectId}/members`,
      );
      return res.data;
    },
    onMutate: async () => {
      const previous = await snapshot();
      qc.setQueryData<WorkshopProjectMembers>(key, (current) =>
        current ? { ...current, members: [] } : current,
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
      failed("Couldn't stop collaborating")(error);
    },
    onSettled: settle,
  });

  return {
    owner: query.data?.owner ?? null,
    members: query.data?.members ?? [],
    isLoading: query.isLoading,
    addMembers,
    removeMember,
    removeAll,
  };
}
